import React from 'react';
import clsx from 'clsx';
import { Colors, FontSize, FontWeight, Spacing } from '@common/tokens';
import { Animation, Icon } from '@common/components';

export type CollapseKey = string | number;

export type CollapsePanelProps = {
  panelKey?: CollapseKey;
  title: React.ReactNode;
  collapsible?: 'disabled';
  defaultOpen?: boolean;
  kind?: 'default' | 'strip' | 'inline';
  summary?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

// NOTE: collapse.tsx injects these through cloneElement, which type-checks nothing, so a
// rename here breaks the container silently.
type InternalPanelProps = {
  _isOpen?: boolean;
  _onToggle?: () => void;
  _bodyId?: string;
};

export function CollapsePanel({
  title,
  collapsible,
  kind = 'default',
  summary,
  className,
  children,
  _isOpen = false,
  _onToggle,
  _bodyId,
}: CollapsePanelProps & InternalPanelProps) {
  const isStrip = kind === 'strip';
  const isInline = kind === 'inline';
  const disabled = collapsible === 'disabled';
  const hasTrailingChevron = isStrip || isInline;

  const icon =
    isStrip || isInline ? (
      <Animation active={_isOpen} kind="rotate-right" rotationDegree={180}>
        <Icon
          size="sm"
          icon="chevronDown"
          color="midnightLighter"
          kind={isStrip ? 'solid' : 'light'}
        />
      </Animation>
    ) : (
      <Animation active={_isOpen} kind="rotate-right" rotationDegree={90}>
        <Icon icon="angleRight" kind="light" color="elm" />
      </Animation>
    );

  return (
    <div
      className={clsx('wk-collapsible__panel', className, {
        'wk-collapsible__panel--open': _isOpen,
        'wk-collapsible__panel--strip': isStrip,
        'wk-collapsible__panel--inline': isInline,
        'wk-collapsible__panel--disabled': disabled,
      })}
    >
      <button
        type="button"
        className="wk-collapsible__trigger"
        onClick={() => _onToggle?.()}
        aria-expanded={_isOpen}
        aria-controls={_bodyId}
        disabled={disabled}
      >
        {!hasTrailingChevron && (
          <span className="wk-collapsible__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="wk-collapsible__title">{title}</span>
        {summary !== undefined && summary !== null && (
          <span className="wk-collapsible__summary">{summary}</span>
        )}
        {hasTrailingChevron && (
          <span className="wk-collapsible__chevron" aria-hidden="true">
            {icon}
          </span>
        )}
      </button>
      <div
        className={clsx('wk-collapsible__reveal', {
          'wk-collapsible__reveal--open': _isOpen,
        })}
        {...(!_isOpen && { inert: '' })}
      >
        <div className="wk-collapsible__reveal-inner">
          <div id={_bodyId} className="wk-collapsible__body" role="region">
            {children}
          </div>
        </div>
      </div>
      <style jsx>{`
        .wk-collapsible__trigger {
          display: flex;
          align-items: center;
          gap: ${Spacing.sm}px;
          width: 100%;
          padding: ${Spacing.sm}px 0;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          color: ${Colors.textDefault};
          font: inherit;
          outline: none;
        }
        .wk-collapsible__trigger:disabled {
          cursor: default;
        }
        .wk-collapsible__trigger:focus-visible {
          outline: 2px solid ${Colors.elm};
          outline-offset: -2px;
        }
        .wk-collapsible__title {
          min-width: 0;
          color: ${Colors.elm};
          font-size: ${FontSize.md};
          font-weight: ${FontWeight.medium};
        }
        .wk-collapsible__summary {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          column-gap: ${Spacing.lg}px;
          row-gap: ${Spacing.sm}px;
          min-width: 0;
        }
        .wk-collapsible__icon,
        .wk-collapsible__chevron {
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        :global(.wk-collapsible__icon .wk-icon) {
          margin-top: 3px;
          margin-bottom: -3px;
        }
        .wk-collapsible__reveal {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.25s ease-in-out;
        }
        .wk-collapsible__reveal--open {
          grid-template-rows: 1fr;
        }
        .wk-collapsible__reveal-inner {
          overflow: hidden;
          min-height: 0;
        }
        .wk-collapsible__body {
          padding: ${Spacing.sm}px 0;
        }

        /* ── strip variant: full-width disclosure band ── */
        .wk-collapsible__panel--strip {
          background: ${Colors.white};
          border-bottom: 1px solid ${Colors.steelLighter};
        }
        .wk-collapsible__panel--strip .wk-collapsible__trigger {
          gap: ${Spacing.lg}px;
          padding: ${Spacing.sm}px ${Spacing.lg}px;
          transition: background-color 200ms;
        }
        .wk-collapsible__panel--strip .wk-collapsible__trigger:hover {
          background-color: ${Colors.steelLightest};
        }
        .wk-collapsible__panel--strip .wk-collapsible__title {
          flex-shrink: 0;
          color: ${Colors.midnightDarkest};
          font-size: ${FontSize.sm};
          font-weight: ${FontWeight.bold};
          white-space: nowrap;
        }
        .wk-collapsible__panel--strip .wk-collapsible__summary {
          flex: 1;
        }
        /* Pin the chevron to the far right whether or not a summary is present
           (the summary, when there, fills the gap; otherwise auto margin does). */
        .wk-collapsible__panel--strip .wk-collapsible__chevron {
          margin-left: auto;
        }
        .wk-collapsible__panel--strip .wk-collapsible__body {
          padding: ${Spacing.md}px ${Spacing.lg}px;
          border-top: 1px solid ${Colors.steelLighter};
          background: ${Colors.steelLightest};
        }

        /* ── inline variant: ghost text disclosure ── */
        /* Hover recoloring lives on the trigger (title inherits) so every selector
           stays single-line — styled-jsx mangles selectors wrapped across lines. */
        .wk-collapsible__panel--inline .wk-collapsible__trigger {
          display: inline-flex;
          width: auto;
          gap: ${Spacing.xs}px;
          padding: 0;
          color: ${Colors.midnightLighter};
        }
        .wk-collapsible__panel--inline .wk-collapsible__trigger:hover {
          color: ${Colors.midnightRegular};
        }
        .wk-collapsible__panel--inline .wk-collapsible__title {
          color: inherit;
          font-size: ${FontSize.sm};
          font-weight: ${FontWeight.light};
        }
        .wk-collapsible__panel--inline .wk-collapsible__body {
          padding: ${Spacing.xs}px 0 0;
        }
      `}</style>
    </div>
  );
}
